import React, { useState, useEffect } from 'react';
import { FaBars } from 'react-icons/fa';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import { primaryItems } from './SidebarItems';
import { getUserPermissions } from '../Authentication/auth';

const Sidebar = ({ isCollapsed, toggleSidebar, onShowTooltip, onHideTooltip }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [userPermissions, setUserPermissions] = useState([]);
  const [dropdownState, setDropdownState] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize to detect mobile/tablet
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      const permissions = await getUserPermissions();
      setUserPermissions(permissions);
    };
    fetchPermissions();
  }, []);

  // Clear dropdowns when collapsing to prevent visual bugs
  useEffect(() => {
    if (isCollapsed) {
      setDropdownState({});
    }
  }, [isCollapsed]);

  const toggleDropdown = (key) => {
    setDropdownState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleMainItemClick = (item, key) => {
    if (item.subItems) {
      toggleDropdown(key);
    } else {
      const hasPermission = userPermissions.includes(item.permission);
      if (hasPermission) navigate(item.path);
      else navigate('/dashboard/unauthorized');
    }
  };

  const handleSubItemNavigation = (subItem) => {
    const hasPermission = userPermissions.includes(subItem.permission);
    if (hasPermission) navigate(subItem.path);
    else navigate('/dashboard/unauthorized');
  };

  const isActive = (path) => location.pathname === path;

  const isParentActive = (item) => {
    return (
      isActive(item.path) ||
      (item.subItems &&
        item.subItems.some(
          (subItem) => isActive(subItem.path) || (subItem.subItems && isParentActive(subItem))
        ))
    );
  };

  const handleMouseEnter = (e, text) => {
    if (isCollapsed && onShowTooltip && windowWidth >= 768) {
      const rect = e.currentTarget.getBoundingClientRect();
      onShowTooltip(text, {
        x: rect.right,
        y: rect.top + rect.height / 2,
      });
    }
    setHoveredItem(text);
  };

  const handleMouseLeave = () => {
    if (onHideTooltip) {
      onHideTooltip();
    }
    setHoveredItem(null);
  };

  // Expanded mode - render sub-items
  const renderSubItems = (subItems, parentKey = '') => {
    return (
      <ul className="ml-6 space-y-1 mt-1">
        {subItems.map((subItem) => {
          const subItemKey = `${parentKey}-${subItem.name}`;
          const hasPermission = userPermissions.includes(subItem.permission);
          if (!hasPermission) return null;

          return (
            <li key={subItemKey} className="relative">
              <div
                className={`flex items-center p-2 text-sm rounded cursor-pointer transition-colors duration-300 ${
                  isActive(subItem.path)
                    ? 'bg-gray-300 text-black font-semibold'
                    : 'hover:bg-gray-200'
                }`}
                onClick={() =>
                  subItem.subItems ? toggleDropdown(subItemKey) : handleSubItemNavigation(subItem)
                }
                onMouseEnter={(e) => handleMouseEnter(e, subItem.name)}
                onMouseLeave={handleMouseLeave}
              >
                {subItem.icon}
                <span className="ml-2">{subItem.name}</span>
                {subItem.subItems && (
                  <span className="ml-auto">
                    {dropdownState[subItemKey] ? (
                      <MdExpandLess className="h-5 w-5" />
                    ) : (
                      <MdExpandMore className="h-5 w-5" />
                    )}
                  </span>
                )}
              </div>

              {subItem.subItems &&
                dropdownState[subItemKey] &&
                renderSubItems(subItem.subItems, subItemKey)}
            </li>
          );
        })}
      </ul>
    );
  };

  // Collapsed mode - render sub-icons
  const renderCollapsedSubIcons = (subItems, parentKey = '', level = 1) => {
    const sizeClasses = {
      1: 'h-5 w-5',
      2: 'h-4 w-4',
      3: 'h-3.5 w-3.5',
    };
    const iconSize = sizeClasses[level] || 'h-3.5 w-3.5';

    return (
      <div className={`flex flex-col items-center mt-2 space-y-1 ${level > 1 ? 'ml-1' : ''}`}>
        {subItems.map((subItem) => {
          const subItemKey = `${parentKey}-${subItem.name}`;
          const hasPermission = userPermissions.includes(subItem.permission);
          if (!hasPermission) return null;
          const isSubItemActive = isActive(subItem.path);

          return (
            <div key={subItemKey} className="flex flex-col items-center relative">
              <div
                className={`p-2 rounded-full cursor-pointer transition-all duration-200 relative group ${
                  isSubItemActive ? 'bg-gray-200 text-gray-800' : 'hover:bg-gray-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (subItem.subItems) toggleDropdown(subItemKey);
                  else handleSubItemNavigation(subItem);
                }}
                onMouseEnter={(e) => handleMouseEnter(e, subItem.name)}
                onMouseLeave={handleMouseLeave}
              >
                <div className={`${iconSize}`}>{subItem.icon}</div>
              </div>

              {subItem.subItems &&
                dropdownState[subItemKey] &&
                renderCollapsedSubIcons(subItem.subItems, subItemKey, level + 1)}
            </div>
          );
        })}
      </div>
    );
  };

  // Check if device is mobile/tablet
  const isMobileOrTablet = windowWidth < 1024;

  return (
    <div
      className={`bg-white text-black p-2 border-r fixed top-0 left-0 h-screen transition-all duration-300 ease-in-out flex flex-col z-50 ${
        isCollapsed ? 'w-20' : 'w-72'
      } ${isMobileOrTablet && isCollapsed ? 'shadow-lg' : ''}`}
    >
      {/* Logo / Top */}
      <div
        className="flex items-center justify-left gap-2 h-16 px-1 cursor-pointer"
        onClick={() => navigate('/dashboard')}
        onMouseEnter={(e) => handleMouseEnter(e, 'Dashboard')}
        onMouseLeave={handleMouseLeave}
      >
        {isCollapsed ? (
          <div className="w-14 h-10 bg-black rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">EQ</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">EQ</span>
            </div>
            <div>
              <h2 className="font-bold text-xl leading-tight">ENTERPRISEQUEST</h2>
              <p className="text-xs text-gray-800 leading-tight">Powered by RhombusQuest</p>
            </div>
          </div>
        )}
      </div>

      {/* Menu */}
      <ul className="mt-2 mb-4 space-y-2 overflow-y-auto custom-scrollbar">
        {primaryItems.map((item) => {
          const itemKey = item.name;
          const isItemActive = isParentActive(item);
          const hasPermission = userPermissions.includes(item.permission);
          if (!hasPermission) return null;

          return (
            <li key={itemKey} className="relative flex flex-col items-center">
              <div
                className={`flex items-center w-full ${
                  isCollapsed ? 'justify-center' : 'justify-between'
                } p-2 rounded cursor-pointer transition-colors duration-300 ${
                  isItemActive ? 'bg-black text-white font-semibold' : 'hover:bg-gray-200'
                }`}
                onClick={() => handleMainItemClick(item, itemKey)}
                onMouseEnter={(e) => handleMouseEnter(e, item.name)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="flex items-center">
                  {item.icon}
                  {!isCollapsed && <span className="ml-3 text-sm">{item.name}</span>}
                </div>

                {!isCollapsed && item.subItems && (
                  <span>
                    {dropdownState[itemKey] ? (
                      <MdExpandLess className="h-5 w-5" />
                    ) : (
                      <MdExpandMore className="h-5 w-5" />
                    )}
                  </span>
                )}
              </div>

              {/* Expanded tree */}
              {!isCollapsed &&
                item.subItems &&
                dropdownState[itemKey] &&
                renderSubItems(item.subItems, itemKey)}

              {/* Collapsed icons (only open branch) */}
              {isCollapsed &&
                item.subItems &&
                dropdownState[itemKey] &&
                renderCollapsedSubIcons(item.subItems, itemKey, 1)}
            </li>
          );
        })}
      </ul>

      {/* Collapse toggle button - Always visible */}
      <div className="mt-auto">
        <button
          onClick={toggleSidebar}
          className={`flex items-center w-full p-2 bg-black text-white ${
            isCollapsed ? 'justify-center' : 'justify-center'
          } hover:bg-gray-800 transition-all`}
          onMouseEnter={(e) => handleMouseEnter(e, isCollapsed ? 'Expand Menu' : 'Collapse Menu')}
          onMouseLeave={handleMouseLeave}
        >
          <FaBars className="h-5 w-5" />
          {!isCollapsed && <span className="ml-3">Collapse Menu</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;

// import React, { useState, useEffect } from 'react';
// import { FaBars } from 'react-icons/fa';
// import { MdExpandMore, MdExpandLess, MdOutlineDashboard } from 'react-icons/md';
// import { useNavigate, useLocation } from 'react-router-dom';
// import { primaryItems } from './SidebarItems';
// import { getUserPermissions } from '../Authentication/auth';

// const Sidebar = ({ isCollapsed, toggleSidebar, onShowTooltip, onHideTooltip }) => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   const [userPermissions, setUserPermissions] = useState([]);
//   const [dropdownState, setDropdownState] = useState({});
//   const [hoveredItem, setHoveredItem] = useState(null);

//   useEffect(() => {
//     const fetchPermissions = async () => {
//       const permissions = await getUserPermissions();
//       setUserPermissions(permissions);
//     };
//     fetchPermissions();
//   }, []);

//   // ✅ simplified toggle that doesn't close others
//   const toggleDropdown = (key) => {
//     setDropdownState((prev) => ({
//       ...prev,
//       [key]: !prev[key],
//     }));
//   };

//   const handleMainItemClick = (item, key) => {
//     if (item.subItems) {
//       toggleDropdown(key);
//     } else {
//       const hasPermission = userPermissions.includes(item.permission);
//       if (hasPermission) navigate(item.path);
//       else navigate('/dashboard/unauthorized');
//     }
//   };

//   const handleSubItemNavigation = (subItem) => {
//     const hasPermission = userPermissions.includes(subItem.permission);
//     if (hasPermission) navigate(subItem.path);
//     else navigate('/dashboard/unauthorized');
//   };

//   const isActive = (path) => location.pathname === path;

//   const isParentActive = (item) => {
//     return (
//       isActive(item.path) ||
//       (item.subItems &&
//         item.subItems.some(
//           (subItem) => isActive(subItem.path) || (subItem.subItems && isParentActive(subItem))
//         ))
//     );
//   };

//   // Handle mouse enter for tooltips
//   const handleMouseEnter = (e, text) => {
//     if (isCollapsed && onShowTooltip) {
//       const rect = e.currentTarget.getBoundingClientRect();
//       onShowTooltip(text, {
//         x: rect.right,
//         y: rect.top + rect.height / 2,
//       });
//     }
//     setHoveredItem(text);
//   };

//   // Handle mouse leave for tooltips
//   const handleMouseLeave = () => {
//     if (onHideTooltip) {
//       onHideTooltip();
//     }
//     setHoveredItem(null);
//   };

//   // EXPANDED MODE (tree)
//   const renderSubItems = (subItems, parentKey = '') => {
//     return (
//       <ul className="ml-6 space-y-1 mt-1">
//         {subItems.map((subItem) => {
//           const subItemKey = `${parentKey}-${subItem.name}`;
//           const hasPermission = userPermissions.includes(subItem.permission);
//           if (!hasPermission) return null;

//           return (
//             <li key={subItemKey} className="relative">
//               <div
//                 className={`flex items-center p-2 text-sm rounded cursor-pointer transition-colors duration-300 ${
//                   isActive(subItem.path)
//                     ? 'bg-gray-300 text-black font-semibold'
//                     : 'hover:bg-gray-200'
//                 }`}
//                 onClick={() =>
//                   subItem.subItems ? toggleDropdown(subItemKey) : handleSubItemNavigation(subItem)
//                 }
//                 onMouseEnter={(e) => handleMouseEnter(e, subItem.name)}
//                 onMouseLeave={handleMouseLeave}
//               >
//                 {subItem.icon}
//                 <span className="ml-2">{subItem.name}</span>
//                 {subItem.subItems && (
//                   <span className="ml-auto">
//                     {dropdownState[subItemKey] ? (
//                       <MdExpandLess className="h-5 w-5" />
//                     ) : (
//                       <MdExpandMore className="h-5 w-5" />
//                     )}
//                   </span>
//                 )}
//               </div>

//               {subItem.subItems &&
//                 dropdownState[subItemKey] &&
//                 renderSubItems(subItem.subItems, subItemKey)}
//             </li>
//           );
//         })}
//       </ul>
//     );
//   };

//   // COLLAPSED MODE — render sub-icons with smaller nested icons
//   const renderCollapsedSubIcons = (subItems, parentKey = '', level = 1) => {
//     const sizeClasses = {
//       1: 'h-5 w-5',
//       2: 'h-4=3 w-4',
//       3: 'h-3.5 w-3.5',
//     };
//     const iconSize = sizeClasses[level] || 'h-3.5 w-3.5';

//     return (
//       <div className={`flex flex-col items-center mt-2 space-y-1 ${level > 1 ? 'ml-1' : ''}`}>
//         {subItems.map((subItem) => {
//           const subItemKey = `${parentKey}-${subItem.name}`;
//           const hasPermission = userPermissions.includes(subItem.permission);
//           if (!hasPermission) return null;
//           const isSubItemActive = isActive(subItem.path);

//           return (
//             <div key={subItemKey} className="flex flex-col items-center relative">
//               {/* Subitem icon with hover tooltip */}
//               <div
//                 className={`p-2 rounded-full cursor-pointer transition-all duration-200 relative group ${
//                   isSubItemActive ? 'bg-gray-200 text-gray-800' : 'hover:bg-gray-100'
//                 }`}
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   if (subItem.subItems) toggleDropdown(subItemKey);
//                   else handleSubItemNavigation(subItem);
//                 }}
//                 onMouseEnter={(e) => handleMouseEnter(e, subItem.name)}
//                 onMouseLeave={handleMouseLeave}
//               >
//                 {/* Clone icon with size scaling */}
//                 <div className={`${iconSize}`}>{subItem.icon}</div>
//               </div>

//               {/* render deeper nested icons */}
//               {subItem.subItems &&
//                 dropdownState[subItemKey] &&
//                 renderCollapsedSubIcons(subItem.subItems, subItemKey, level + 1)}
//             </div>
//           );
//         })}
//       </div>
//     );
//   };

//   return (
//     <div
//       className={`bg-white text-black p-2 border-r fixed top-0 left-0 h-screen transition-all duration-300 ease-in-out flex flex-col ${
//         isCollapsed ? 'w-20' : 'w-72'
//       }`}
//     >
//       {/* Logo / Top */}
//       <div
//         className="flex items-center justify-left gap-2 h-16 px-1 cursor-pointer"
//         onClick={() => navigate('/dashboard')}
//         onMouseEnter={(e) => handleMouseEnter(e, 'Dashboard')}
//         onMouseLeave={handleMouseLeave}
//       >
//         {isCollapsed ? (
//           // Collapsed mode: Show EQ in black box
//           <div className="w-14 h-10 bg-black rounded-lg flex items-center justify-center">
//             <span className="text-white font-bold text-lg">EQ</span>
//           </div>
//         ) : (
//           // Expanded mode: Show EQ box, Enterprise Quest text and Powered by
//           <div className="flex items-center gap-2">
//             <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
//               <span className="text-white font-bold text-lg">EQ</span>
//             </div>
//             <div>
//               <h2 className="font-bold text-xl leading-tight">ENTERPRISEQUEST</h2>
//               <p className="text-xs text-gray-800 leading-tight">Powered by RhombusQuest</p>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Menu */}
//       <ul className="mt-2 mb-4 space-y-2 overflow-y-auto custom-scrollbar">
//         {primaryItems.map((item) => {
//           const itemKey = item.name;
//           const isItemActive = isParentActive(item);

//           return (
//             <li key={itemKey} className="relative flex flex-col items-center">
//               {/* Main item */}
//               <div
//                 className={`flex items-center w-full ${
//                   isCollapsed ? 'justify-center' : 'justify-between'
//                 } p-2 rounded cursor-pointer transition-colors duration-300 ${
//                   isItemActive ? 'bg-black text-white font-semibold' : 'hover:bg-gray-200'
//                 }`}
//                 onClick={() => handleMainItemClick(item, itemKey)}
//                 onMouseEnter={(e) => handleMouseEnter(e, item.name)}
//                 onMouseLeave={handleMouseLeave}
//               >
//                 <div className="flex items-center">
//                   {item.icon}
//                   {!isCollapsed && <span className="ml-3 text-sm">{item.name}</span>}
//                 </div>

//                 {!isCollapsed && item.subItems && (
//                   <span>
//                     {dropdownState[itemKey] ? (
//                       <MdExpandLess className="h-5 w-5" />
//                     ) : (
//                       <MdExpandMore className="h-5 w-5" />
//                     )}
//                   </span>
//                 )}
//               </div>

//               {/* Expanded tree */}
//               {!isCollapsed &&
//                 item.subItems &&
//                 dropdownState[itemKey] &&
//                 renderSubItems(item.subItems, itemKey)}

//               {/* Collapsed icons (only open branch) */}
//               {isCollapsed &&
//                 item.subItems &&
//                 dropdownState[itemKey] &&
//                 renderCollapsedSubIcons(item.subItems, itemKey, 1)}
//             </li>
//           );
//         })}
//       </ul>

//       {/* Collapse toggle */}
//       <div className="mt-auto">
//         <button
//           onClick={toggleSidebar}
//           className="flex items-center w-full p-2 bg-black text-white justify-center hover:bg-gray-800 transition-all"
//           onMouseEnter={(e) => handleMouseEnter(e, isCollapsed ? 'Expand Menu' : 'Collapse Menu')}
//           onMouseLeave={handleMouseLeave}
//         >
//           <FaBars className="h-5 w-5" />
//           {!isCollapsed && <span className="ml-3">Collapse Menu</span>}
//         </button>
//       </div>
//     </div>
//   );
// };

// export default Sidebar;
